import { createRouter, createWebHistory } from 'vue-router'
import { useMainStore, isAuthorityAllowedValue, isAuthorityRestrictedValue } from '@/store'
import { loginPeriodExtend, companyAuthorityLookup } from '@/api/loginAPI'
import VueCookies from 'vue-cookies'
import { useToast } from 'vue-toastification'
import { isDemoCompanyContext } from '@/utils/demoCompany'
import { isExkrCompanyContext } from '@/utils/exkrCompany'
import { clearSession } from '@/utils/session'

// 토큰 유효성 검사
function isValidToken(token) {
  return typeof token === 'string' && token.split('.').length === 3
}
// 토큰 만료 확인 함수 (client_token과 refresh_token 모두 사용 가능)
function isTokenExpired(token) {
  if (!isValidToken(token)) {
    return true // 유효하지 않은 토큰은 만료된 것으로 간주
  }
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(window.atob(base64))
    const expirationDate = new Date(payload.exp * 1000)
    const currentDate = new Date()
    return expirationDate < currentDate
  } catch (error) {
    return true // 문제가 있으면 만료된 것으로 간주
  }
}

// 관리자 전용 페이지 접근 권한 체크 함수
// 역할: meta.adminOnly가 설정된 라우트는 adminLevel === 'admin'만 진입 허용
// 데모 회사는 부관리자 권한 체험을 위해 예외 허용
function checkAdminOnlyAccess(route, authorityData, store) {
  if (!route.meta?.adminOnly) {
    return { allowed: true }
  }
  if (isDemoCompanyContext(store)) {
    return { allowed: true }
  }
  if (authorityData?.adminLevel === 'admin') {
    return { allowed: true }
  }
  return {
    allowed: false,
    message: '관리자 계정만 접근할 수 있는 페이지입니다.'
  }
}

// 요금제 접근 권한 체크 함수
// 역할: 현재 라우트(to)와 내 요금제(myPlan)를 비교하여 접근 가능 여부 판단
function checkPlanAccess(route, myPlan) {
  // 라우트에 설정된 제한 요금제 확인 (meta 정보 활용)
  const requiredPlans = Array.isArray(route.meta?.planTypes)
    ? route.meta.planTypes
    : route.meta?.planType
      ? [route.meta.planType]
      : []

  // 제한이 없는 페이지라면 통과
  if (requiredPlans.length === 0) {
    return { allowed: true }
  }

  // 제한이 있는데 내 요금제가 허용 목록에 없다면 차단
  if (!requiredPlans.includes(myPlan)) {
    return {
      allowed: false,
      message: '현재 요금제에서는 접근할 수 없는 페이지입니다.'
    }
  }

  return { allowed: true }
}

// EXKR/DOGO(도로공사) 회사 컨텍스트 라우트 접근 제어 함수
// - meta.hiddenForExkr: 도로공사에서 숨긴 메뉴를 도로공사 컨텍스트가 직접 접근 시 차단
// - meta.exkrOnly: 도로공사 전용 메뉴(작업 설정)를 비-도로공사 회사가 직접 접근 시 차단
function checkExkrAccess(route, store) {
  const isExkr = isExkrCompanyContext(store)
  if (route.meta?.hiddenForExkr && isExkr) {
    return { allowed: false }
  }
  if (route.meta?.exkrOnly && !isExkr) {
    return { allowed: false }
  }
  return { allowed: true }
}

function isSameOrChildPath(path, targetPath) {
  return path === targetPath || path.startsWith(`${targetPath}/`)
}

function hasPageAuthorityRestriction(authorityData, permissionKey) {
  if (authorityData?.adminLevel === 'admin') {
    return false
  }

  if (isAuthorityRestrictedValue(authorityData?.admin_role)) {
    return true
  }

  return (
    isAuthorityAllowedValue(authorityData?.admin_role) &&
    isAuthorityRestrictedValue(authorityData?.[permissionKey])
  )
}

// 관리자 권한 경고 메시지 출력 함수
// 전역 제한(admin_role === 0)일 때는 토스트를 표시하지 않음 (모든 페이지에서 반복되어 UX 저하)
// 도메인별 개별 제한일 때만 해당 페이지 진입 시 안내
function displayAuthorityWarnings(path, authorityData) {
  // 전역 제한이면 개별 페이지 토스트도 표시하지 않음
  if (
    authorityData?.adminLevel !== 'admin' &&
    isAuthorityRestrictedValue(authorityData?.admin_role)
  ) {
    return
  }

  if (isSameOrChildPath(path, '/drive') && hasPageAuthorityRestriction(authorityData, 'manage_driving')) {
    showCustomToast(
      '부관리자 계정의 주행 기록 페이지의 등록, 수정, 삭제 기능이 제한되어 있습니다.\n자세한 사항은 관리자에게 문의해 주세요.',
      'error'
    )
  }
  if (isSameOrChildPath(path, '/spend') && hasPageAuthorityRestriction(authorityData, 'manage_cbook')) {
    showCustomToast(
      '부관리자 계정의 지출 관리 페이지의 등록, 수정, 삭제 기능이 제한되어 있습니다.\n자세한 사항은 관리자에게 문의해 주세요.',
      'error'
    )
  }
  if (path === '/push' && hasPageAuthorityRestriction(authorityData, 'manage_push')) {
    showCustomToast(
      '부관리자 계정의 푸시 메시지 페이지의 메시지 발송, 수정, 예약 취소 기능이 제한되어 있습니다.\n자세한 사항은 관리자에게 문의해 주세요.',
      'error'
    )
  }
  if (path === '/customer' && hasPageAuthorityRestriction(authorityData, 'manage_customer')) {
    showCustomToast(
      '부관리자 계정의 고객 관리 페이지의 등록, 수정, 삭제 기능이 제한되어 있습니다.\n자세한 사항은 관리자에게 문의해 주세요.',
      'error'
    )
  }
  if (isSameOrChildPath(path, '/customer/cars') && hasPageAuthorityRestriction(authorityData, 'manage_customer_car')) {
    showCustomToast(
      '부관리자 계정의 고객 차량 관리 페이지의 등록, 수정, 삭제 기능이 제한되어 있습니다.\n자세한 사항은 관리자에게 문의해 주세요.',
      'error'
    )
  }
}

/*
 * to: 이동할 url 정보가 담긴 라우터 객체
 * from: 현재 url 정보가 담긴 라우터 객체
 * next: to에서 지정한 url로 이동하기 위해 꼭 호출해야 하는 함수
 * next() 가 호출되기 전까지 화면 전환되지 않음
 */
// 네비게이션 가드 함수 (cookie)
async function requireAuth(to, from, next) {
  const publicPages = ['/login', '/signup', '/pw', '/find', '/gotopayment']
  const authRequired = !publicPages.includes(to.path)

  let token = VueCookies.get('client_token')
  let refreshToken = VueCookies.get('refresh_token')

  // 아래 토큰 갱신에 성공하면 재계산한다(let 인 이유).
  // const 로 고정돼 있던 동안에는 갱신에 성공해도 이 값이 false 로 남아,
  // 마지막 `authRequired && !isTokenValid` 에서 그대로 강제 로그아웃됐다(= "로그인 유지"가 동작하지 않았다).
  let isTokenValid = token && isValidToken(token) && !isTokenExpired(token)
  const isRefreshTokenValid =
    refreshToken && isValidToken(refreshToken) && !isTokenExpired(refreshToken)

  // 중복 로직을 함수로 분리
  const redirectToLogin = () => {
    // 만료된 세션 흔적을 제거합니다(SPA 자격증명 — client_token/refresh_token/loginData + localStorage).
    //
    // 결제 핸드오프 쿠키(auth_token/biz_car/progress_id/plan_type)는 의도적으로 남깁니다(명시적 로그아웃에서만 정리).
    // 이 함수는 "토큰 만료"뿐 아니라 "미로그인 상태로 보호 라우트 접근"에서도 실행되는데,
    // 결제 대기 계정은 /gotopayment 에서 결제 배너를 새 탭으로 띄워 둔 상태일 수 있습니다.
    // 그 상태에서 원래 탭이 아무 페이지로 이동하기만 해도 여기로 들어오므로,
    // 핸드오프 값까지 지우면 살아 있는 배너 탭의 결제 플로우가 끊깁니다.
    // auth_token 은 배너가 결제 승인 "이후"에도 요금제 반영에 쓰는 배너 소유 쿠키라 특히 그렇습니다
    // (근거는 utils/session.js 의 PAYMENT_HANDOFF_COOKIE_KEYS 주석 참고).
    clearSession()
    // Pinia 스토어 초기화. ⚠️ 반드시 clearSession() "이후"에 호출한다 — 순서를 바꾸면 조용히 무효가 됩니다.
    // $reset() 은 state() 를 재실행하는데 store/index.js 의 loginAccount/planType 이
    // localStorage.getItem(...) 으로 초기화되므로, 먼저 돌면 방금 지울 값들을 도로 읽어 들입니다.
    // (headerView.logout() 도 같은 순서로 맞춰 두었습니다 — 두 경로가 갈라지지 않게 함께 수정합니다)
    useMainStore().$reset()
    showCustomToast('보안을 위해, 다시 로그인해 주세요.', 'error')
    return next('/login')
  }

  // 토큰 갱신 시도
  if (!isTokenValid && isRefreshTokenValid) {
    try {
      const newToken = await loginPeriodExtend(token, refreshToken)
      if (newToken) {
        VueCookies.set('client_token', newToken)
        token = newToken
        // 서버가 준 토큰도 같은 기준으로 다시 검사한다(무조건 true 로 두지 않는다).
        // 형식이 깨졌거나 이미 만료된 토큰을 받으면 false 로 남아 아래에서 정상적으로 재로그인 처리된다.
        isTokenValid = isValidToken(token) && !isTokenExpired(token)
      } else {
        return redirectToLogin()
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      return redirectToLogin()
    }
  }

  // 로그인 후 특정 페이지 접근 제한
  const shouldRedirectToHome = publicPages.includes(to.path) && isTokenValid
  if (shouldRedirectToHome) {
    return next('/')
  }

  // 인증이 필요한 페이지 진입 시 검사 로직
  if (authRequired && isTokenValid) {
    const store = useMainStore()

    // 요금제(Plan) 체크
    const myPlan =
      store.getLoginData?.response?.body?.plan_type ||
      localStorage.getItem('planType') ||
      'employee'

    const planCheck = checkPlanAccess(to, myPlan)

    if (!planCheck.allowed) {
      showCustomToast(planCheck.message, 'error')
      return next('/') // 차단 후 메인으로 이동
    }

    // EXKR/DOGO(도로공사) 숨김 메뉴 직접 접근 차단 (메뉴에 없는 페이지이므로 토스트 없이 메인 이동)
    const exkrCheck = checkExkrAccess(to, store)
    if (!exkrCheck.allowed) {
      return next('/')
    }

    // 관리자 권한 조회 및 경고 (API 호출)
    try {
      const authorityResponse = await companyAuthorityLookup()
      const authorityData = authorityResponse?.data?.response?.body?.items
      if (!authorityData) {
        if (to.meta?.adminOnly) {
          showCustomToast('관리자 권한을 확인하지 못해 접근할 수 없습니다. 잠시 후 다시 시도해 주세요.', 'error')
          return next('/')
        }
      } else {
        store.saveAuthorityData(authorityData) // Pinia 상태 업데이트
        // adminOnly 라우트(meta.adminOnly === true)는 부관리자 접근 차단
        const adminOnlyCheck = checkAdminOnlyAccess(to, authorityData, store)
        if (!adminOnlyCheck.allowed) {
          showCustomToast(adminOnlyCheck.message, 'error')
          return next('/')
        }
        displayAuthorityWarnings(to.path, authorityData)
      }
    } catch (error) {
      console.error('Authority lookup error:', error)
      if (to.meta?.adminOnly) {
        showCustomToast('관리자 권한을 확인하지 못해 접근할 수 없습니다. 잠시 후 다시 시도해 주세요.', 'error')
        return next('/')
      }
    }
  }

  // 로그인 필요 페이지 접근 시 토큰이 없거나 만료된 경우
  if (authRequired && !isTokenValid) {
    return redirectToLogin()
  }

  next()
}

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    },
    {
      // 로그인
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue')
    },
    {
      // 회원가입
      path: '/signup',
      name: 'signup',
      component: () => import('@/views/SignUpView.vue')
    },
    {
      // 비밀번호 변경
      path: '/pw',
      name: 'PasswordView',
      component: () => import('@/views/PasswordView.vue')
    },
    {
      // 비밀번호 찾기
      path: '/find',
      name: 'PasswordFindView',
      component: () => import('@/views/PasswordFindView.vue')
    },
    {
      // 결제 안내 페이지
      path: '/gotopayment',
      name: 'GoToPaymentView',
      component: () => import('@/views/GoToPaymentView.vue')
    },
    {
      // 대시보드 V2
      path: '/',
      name: 'Home',
      component: () => import(/* webpackPrefetch: true */ '@/views/_DashboardView_v2.vue') // 동적 import
    },
    {
      // 조직 관리 - 임직원 관리 요금제 전용
      path: '/group',
      name: 'group',
      component: () => import(/* webpackPrefetch: true */ '@/views/GroupView.vue'),
      meta: { planType: 'employee' }
    },
    {
      // 임직원 관리
      path: '/employees',
      name: 'employees',
      component: () => import(/* webpackPrefetch: true */ '@/views/EmployeesView.vue'),
      meta: { planType: 'employee' }
    },
    {
      // 차량 관리 - 임직원 관리 요금제 전용
      path: '/cars',
      name: 'cars',
      component: () => import(/* webpackPrefetch: true */ '@/views/CarsView.vue'),
      meta: { planType: 'employee' }
    },
    {
      // 주행 기록
      path: '/drive',
      name: 'drive',
      component: () => import(/* webpackPrefetch: true */ '@/views/DriveView.vue')
    },
    {
      // 주행 중 데이터
      path: '/drive-data/:id',
      name: 'drive-data',
      component: () => import(/* webpackPrefetch: true */ '@/components/drive/DriveDataView.vue')
    },
    {
      // 진단 기록 (신규 UI/UX)
      path: '/diagnosis',
      name: 'diagnosis',
      component: () => import(/* webpackPrefetch: true */ '@/views/DiagnosisViewV2.vue')
    },
    {
      // 진단 기록 상세 (신규 UI/UX)
      // /diagnosis 가 차량 통계에서 넘어오는 쿼리(carId/from/to)를 이미 쓰고 있어 별도 경로로 분리한다.
      path: '/diagnosis-detail/:id',
      name: 'diagnosis-detail',
      component: () => import(/* webpackPrefetch: true */ '@/views/DiagnosisDetailView.vue')
    },
    {
      // 진단 기록 (구) — 신규 페이지 개발이 모두 끝날 때까지 참고용으로 유지
      path: '/diagnosis-old',
      name: 'diagnosis-old',
      component: () => import(/* webpackPrefetch: true */ '@/views/DiagnosisView.vue'),
      meta: { hiddenForExkr: true }
    },
    {
      // 실시간 관제
      path: '/control',
      name: 'control',
      component: () => import(/* webpackPrefetch: true */ '@/views/ControlView.vue'),
      meta: { hiddenForExkr: true }
    },
    {
      // 지출 관리 - 임직원 관리 요금제 전용
      path: '/spend',
      name: 'spend',
      component: () => import(/* webpackPrefetch: true */ '@/views/SpendView.vue'),
      meta: { planType: 'employee', hiddenForExkr: true }
    },
    {
      // 결제 관리
      path: '/bizpay',
      name: 'bizpay',
      component: () => import(/* webpackPrefetch: true */ '@/views/BizPayView.vue'),
      meta: { hiddenForExkr: true }
    },
    {
      // 스케줄링 설정
      path: '/schedule',
      name: 'schedule',
      component: () => import(/* webpackPrefetch: true */ '@/views/ScheduleView.vue'),
      meta: { hiddenForExkr: true }
    },
    {
      // 자동 진단 설정
      path: '/autodiagnosis',
      name: 'autodiagnosis',
      component: () => import(/* webpackPrefetch: true */ '@/views/AutoDiagSetView.vue'),
      meta: { hiddenForExkr: true }
    },
    {
      // 시스템 설정 - 부관리자 권한 부여/회사 정보 수정이 포함되므로 관리자만 진입 허용
      path: '/settings',
      name: 'settings',
      component: () => import(/* webpackPrefetch: true */ '@/views/SettingsView.vue'),
      meta: { adminOnly: true, hiddenForExkr: true }
    },
    {
      // 배차 관리 - 임직원 관리 요금제 전용
      path: '/dispatch',
      name: 'dispatch',
      component: () => import(/* webpackPrefetch: true */ '@/views/DispatchView.vue'),
      meta: { planType: 'employee', hiddenForExkr: true }
    },
    {
      // 고객 관리
      path: '/customer',
      name: 'customer',
      component: () => import(/* webpackChunkName: "customer" */ '@/views/CustomerView.vue'),
      meta: { planType: 'customer' }
    },
    {
      // 관리자 관리 - 관리자 계정 등록/수정/삭제 권한이 부관리자에게 노출되지 않도록 admin 전용으로 제한
      path: '/customer/admin',
      name: 'customer-admin',
      component: () => import(/* webpackChunkName: "admin" */ '@/views/AdminView.vue'),
      meta: { planType: 'customer', adminOnly: true }
    },
    {
      // 차량 관리 - 고객 관리
      path: '/customer/cars',
      name: 'customer-cars',
      component: () =>
        import(/* webpackChunkName: "customer-cars" */ '@/views/CustomerCarsView.vue'),
      meta: { planType: 'customer' }
    },
    {
      // 통계 분석 - 운전자 통계
      path: '/stats/driver',
      name: 'driver-stats',
      component: () => import(/* webpackChunkName: "driver-stats" */ '@/views/DriverStats.vue'),
      meta: { planTypes: ['employee', 'customer'] }
    },
    {
      // 통계 분석 - 차량 통계
      path: '/stats/car',
      name: 'car-stats',
      component: () => import(/* webpackChunkName: "car-stats" */ '@/views/CarStats.vue'),
      meta: { planTypes: ['employee', 'customer'] }
    },
    {
      // 푸시 메시지
      path: '/push',
      name: 'push',
      component: () => import(/* webpackChunkName: "push" */ '@/views/PushView.vue'),
      meta: { planTypes: ['employee', 'customer'], hiddenForExkr: true }
    },
    {
      // 메시지 보내기
      path: '/push/send',
      name: 'push-send',
      component: () => import(/* webpackChunkName: "push-send" */ '@/views/PushMessageSendView.vue'),
      meta: { planTypes: ['employee', 'customer'], hiddenForExkr: true }
    },
    {
      // 작업 설정 > VMS 관리 (도로공사 EXKR/DOGO 전용)
      // 2026-07-22: 공종/VMS 데이터를 태블릿 앱 내부 DB로 이관하면서 웹 화면은 미노출 처리(파일은 보존, 추후 복원 가능)
      path: '/exkr/vms',
      name: 'exkr-vms',
      component: () => import(/* webpackChunkName: "exkr-vms" */ '@/views/exkr/VmsView.vue'),
      meta: { exkrOnly: true, hiddenForExkr: true }
    },
    {
      // 작업 설정 > 업무 상태 관리 (공종, 도로공사 EXKR/DOGO 전용)
      // 2026-08-21: 웹 화면 미노출 처리(파일은 보존, 추후 복원 가능)
      path: '/exkr/work-type',
      name: 'exkr-work-type',
      component: () => import(/* webpackChunkName: "exkr-work-type" */ '@/views/exkr/WorkTypeView.vue'),
      meta: { exkrOnly: true, hiddenForExkr: true }
    },
    {
      // 작업 설정 > 위치변환 관리 (도로공사 EXKR/DOGO 전용)
      // 2026-07-22: 공종/VMS 데이터를 태블릿 앱 내부 DB로 이관하면서 웹 화면은 미노출 처리(파일은 보존, 추후 복원 가능)
      path: '/exkr/location',
      name: 'exkr-location',
      component: () => import(/* webpackChunkName: "exkr-location" */ '@/views/exkr/LocationView.vue'),
      meta: { exkrOnly: true, hiddenForExkr: true }
    }
    // 테스트 페이지
    // {
    //   // 차량 관제 테스트
    //   path: '/car-control-setting',
    //   name: 'TestKakaoMap',
    //   component: () => import('@/views/TestCarControlSet.vue')
    // },
    // {
    //   // 소켓 테스트
    //   path: '/socket-test-place',
    //   name: 'TestSocekPlace',
    //   component: () => import('@/views/TestSocekPlace.vue')
    // }
    // {
    //   // 공지사항
    //   path: '/announce',
    //   name: 'announce',
    //   component: () => import(/* webpackChunkName: "announce" */ '@/views/AnnounceView.vue')
    // },
    // {
    //   // 부관리자 설정
    //   path: '/authsettings',
    //   name: 'authsettings',
    //   component: () => import(/* webpackChunkName: "settings" */ '@/views/SettingsAuthView.vue')
    // },
    // {
    // // 소모품 기록
    //   path: '/expend',
    //   name: 'expend',
    //   component: () =>
    //     import(/* webpackChunkName: "expend" */ '@/views/ExpendView.vue')
    // },
    // {
    //   // 대시보드 구 버전
    //   path: '/sample',
    //   name: 'dashboard_v1',
    //   component: () => import(/* webpackChunkName: "home2" */ '@/views/_DashboardView.vue')
    // },
    // {
    //   // 스캐너 관리
    //   path: '/scanners',
    //   name: 'scanners',
    //   component: () => import(/* webpackChunkName: "scanners" */ '@/views/ScannersView.vue')
    // },
    // {
    //   // 정비
    //   path: '/repair',
    //   name: 'repair',
    //   component: () => import(/* webpackChunkName: "repair" */ '@/views/RepairView.vue')
    // },
    // {
    //   // 나의 정보
    //   path: '/info',
    //   name: 'info',
    //   component: () => import(/* webpackChunkName: "info" */ '@/views/InfoView.vue')
    // },
    //   // 배차 관련
    // {
    //   path: '/assign',
    //   name: 'assign',
    //   component: () =>
    //     import(
    //       /* webpackChunkName: "assign", webpackPrefetch:true */ '@/views/AssignView.vue'
    //     )
    // },
    // {
    //   path: '/assign_record',
    //   name: 'assign_record',
    //   component: () =>
    //     import(
    //       /* webpackChunkName: "assign_record", webpackPrefetch:true */ '@/views/AssignRecordView.vue'
    //     )
    // },
  ]
})

function showCustomToast(content, type) {
  const toast = useToast()
  const customToastOptions = {
    position: 'top-center',
    timeout: 2504,
    closeOnClick: false,
    pauseOnFocusLoss: false,
    pauseOnHover: false,
    draggable: false,
    draggablePercent: 0.6,
    showCloseButtonOnHover: false,
    hideProgressBar: true,
    closeButton: false,
    icon: true,
    rtl: false
  }
  switch (type) {
    case 'error':
      toast.error(content, customToastOptions)
      break
    case 'info':
      toast.info(content, customToastOptions)
      break
    case 'warning':
      toast.warning(content, customToastOptions)
      break
    default:
      toast.info(content, customToastOptions)
      break
  }
}

router.beforeEach(requireAuth)

export default router
